/**
 * Initialises MSAL from the auth config and wraps children in <MsalProvider>.
 *
 * MSAL is built once per page load — re-initialising mid-page would race the
 * token cache. We memoise on `spa_client_id` + `authority` so a
 * config change forces a fresh instance (uncommon, useful in tests).
 *
 * Token cache is the tab's sessionStorage (ADR-0053): it survives a refresh
 * and is gone when the tab or browser closes. Never localStorage, which would
 * outlive the browser. Silent renewal uses the iframe flow.
 */

import { useEffect, useMemo } from "react";
import type { PropsWithChildren } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { setUnauthorisedHandler } from "@/shared/api/apiFetch";
import { loginPathFor } from "./session";
import { useAuthConfig } from "./useAuthConfig";

export function MsalBootstrap({ children }: PropsWithChildren) {
  const config = useAuthConfig();

  const instance = useMemo(() => {
    return new PublicClientApplication({
      auth: {
        clientId: config.spa_client_id,
        authority: config.authority,
        redirectUri: pickRedirectUri(config.redirect_uris),
      },
      cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false,
      },
    });
  }, [config.spa_client_id, config.authority, config.redirect_uris]);

  // Wire the apiFetch 401 interceptor to MSAL. When the API rejects a
  // request as unauthenticated we drop the cached MSAL session — the
  // token has either drifted, the account was revoked server-side, or
  // the user's session expired. We clear silently rather than triggering
  // a popup because the user has already been logged out by definition.
  //
  // `session_ended` means the server ended the session (sign-out elsewhere,
  // the idle limit, deactivation — ADR-0046). The same token will never
  // work again, and a silent renewal would reuse the old sign-in, so the
  // person is sent to /login, which asks Microsoft for a fresh one.
  useEffect(() => {
    setUnauthorisedHandler((code) => {
      void instance.clearCache().finally(() => {
        if (code === "session_ended" && !window.location.pathname.startsWith("/login")) {
          window.location.assign(loginPathFor("session_ended"));
        }
      });
    });
    return () => setUnauthorisedHandler(null);
  }, [instance]);

  return <MsalProvider instance={instance}>{children}</MsalProvider>;
}

function pickRedirectUri(allowed: string[]): string {
  if (typeof window === "undefined") return allowed[0] ?? "";
  const origin = window.location.origin;
  // Prefer an exact origin match, fall back to anything starting with origin,
  // then to the first allowed value.
  return (
    allowed.find((u) => u === origin) ??
    allowed.find((u) => u.startsWith(origin)) ??
    allowed[0] ??
    origin
  );
}
